import React, { useMemo } from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom";

import Divider from "antd/es/divider";
import Tooltip from "antd/es/tooltip";
import Tag from "antd/es/tag";

import colors from "config/colors";
// import doorState from 'config/doorState';
import unloadSpeed from "config/unloadSpeed";

import PageHeader from "components/MainPageHeader";
import { LoadingText, Text } from "components/Text";
import { Space } from "components/layout";
import Button from "components/Button";
import Icon from "components/Icon";

import ProcessBar from "./ProcessBar";
// import DirectionTag from './DirectionTag';

import {
  EQUIPMENT_TYPE_LABELS,
  PROCESS_DIRECTION_LABELS,
  isContainer,
  isLoad,
  isTrailer,
  isUnload,
} from "helpers/process";

const StyledPageHeader = styled(PageHeader)`
  padding-bottom: 8px;
  padding-top: 24px;

  & .ant-page-header-heading {
    min-height: 60px;
    max-height: 60px;
    margin-bottom: 8px;
  }

  & .ant-page-header-heading-sub-title {
    margin-bottom: 8px;
  }

  & .ant-page-header-heading-extra {
    margin-top: 8px;
  }

  & .ant-page-header-content {
    padding-top: unset;
    margin-top: -8px;
  }
`;

const DoorNameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 12px;
`;

const ProcessCodeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 12px;
`;

const ProcessDetailsPageHeader = ({
  process,
  processId,
  children,
  ...rest
}) => {
  const history = useHistory();
  const info = useMemo(() => {
    let info = {
      inProgress: false,
      isCompleted: false,
      hasAnomaly: false,
      hasUnidentifiedEmployees: false,
    };

    if (process) {
      info = {
        ...info,
        isUnload: isUnload(process),
        isLoad: isLoad(process),
        isContainer: isContainer(process),
        isTrailer: isTrailer(process),
      };

      const isCompleted = process.container_state === "FINISHED";
      const inProgress =
        process.container_state === "LOADING" ||
        process.container_state === "UNLOADING" ||
        process.container_state === "STOPPED";

      if (inProgress) {
        info.inProgress = true;
        info.speedColor = unloadSpeed.getColor(process.relative_speed);
        info.unloadedPerc = Math.floor(process.unloaded_percent * 100);
        info.unloadedPercForcast = Math.floor(
          process.unloaded_percent_forecast * 100
        );
      } else if (isCompleted) {
        info.isCompleted = true;
        if (info.isUnload) {
          info.completedMessage = process.speed_msg;
        }
      }

      info.hasAnomaly = process.is_low_accuracy === 1;
      info.warningMessage = process.warning_message || "";
      info.hasUnidentifiedEmployees =
        (process.unidentified_employees || 0) !== 0;
    }
    return info;
  }, [process]);
  
  return (
    <StyledPageHeader
      title={
        <Space size={16}>
          {process ? (
            <DoorNameWrapper>
              <Text style={{ color: "#A4B0BA" }}>Door {process.door_name}</Text>
              <Text
                style={{
                  fontSize: "12px",
                  lineHeight: 1.5,
                  color: "#A4B0BA",
                  fontWeight: 400,
                  marginLeft: "2px",
                }}
              >
                {PROCESS_DIRECTION_LABELS[process.direction] || ""}
              </Text>
            </DoorNameWrapper>
          ) : (
            <LoadingText
              style={{
                display: "inline-block",
                paddingBottom: "8px",
                color: "#A4B0BA",
                marginRight: "16px",
              }}
            >
              Loading
            </LoadingText>
          )}
          {process && (
            <Divider type="vertical" style={{ height: "40px", margin: 0 }} />
          )}
          {process && (
              <ProcessCodeWrapper>
                <Text
                  style={{
                    display: "inline-block",
                    maxWidth: "13ch",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {process.code}
                </Text>
                <Text
                  style={{
                    fontSize: "12px",
                    lineHeight: 1.5,
                    color: "#A4B0BA",
                    fontWeight: 400,
                    marginLeft: "2px",
                  }}
                >
                  {process ? EQUIPMENT_TYPE_LABELS[process.container_type] : ""}
                </Text>
              </ProcessCodeWrapper>
          )}
          {info.hasUnidentifiedEmployees && (
            <Tooltip title="Unidentified individual">
              <Icon
                name="user"
                size={16}
                fill="#CF1322"
                color="white"
                strokeWidth="3"
                style={{
                  backgroundColor: "#CF1322",
                  borderRadius: "32px",
                  width: "24px",
                  height: "24px",
                  display: "inline-flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginBottom: "18px",
                }}
              />
            </Tooltip>
          )}
          {(info.warningMessage || info.hasAnomaly) && (
            <Tooltip title={info.hasAnomaly?'Low accuracy':info.warningMessage}>
              <Icon
                name="alert-triangle"
                size={20}
                fill="yellow"
                color="black"
                strokeWidth="3"
                style={{
                  borderRadius: "32px",
                  width: "24px",
                  height: "24px",
                  display: "inline-flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginBottom: "18px",
                }}
              />
            </Tooltip>
          )}
          {info.inProgress ? (
            <ProcessBar
              completed={info.unloadedPerc}
              completedColor={info.isLoad ? "#232526" : info.speedColor}
              forcasted={info.unloadedPercForcast}
              forcastedColor={"#828C94"}
              showPrediction={info.isUnload}
              style={{
                width: "140px",
                marginBottom: "4px",
              }}
            />
          ) : info.isCompleted ? (
            <Tag
              color={"green"}
              style={{
                display: "flex",
                flexDirection: "column",
                marginBottom: "6px",
              }}
            >
              <span
                style={{
                  lineHeight: 1.5,
                }}
              >
                Completed
              </span>
              {info.completedMessage && (
                <span
                  style={{
                    fontWeight: "normal",
                    lineHeight: 1.5,
                  }}
                >
                  ({info.completedMessage})
                </span>
              )}
            </Tag>
          ) : null}
        </Space>
      }
      backIcon={
        <Button
          shape="circle"
          size="large"
          icon={{ name: "arrow-left", color: colors.grey.lightDark, size: 18 }}
          style={{
            marginBottom: "4px",
          }}
        />
      }
      onBack={() => {
        history.goBack();
      }}
      {...rest}
    >
      {/* <StatusTag container={stats.container} style={{ marginLeft: '45px' }} /> */}
      {/* <div style={{ marginLeft: '58px', height: '24px', marginTop: '6px' }}>
        {process && process.direction && <DirectionTag process={process} />}
      </div> */}
      {children}
    </StyledPageHeader>
  );
};

export default ProcessDetailsPageHeader;
